import React from 'react'
import { Alert, Button } from '@mui/material'
import { Link } from 'react-router-dom'
import { useGetBusiness } from '@marketing-milk/frontend'
import { CreativeImportJobStatus, CreativeImportRowStatus } from '@marketing-milk/interfaces'
import { useSprintPackets } from 'hooks/api/useSprintPackets'
import { CreativeImportState } from '../state'

interface Props {
  state: CreativeImportState
  onImportAnother: () => void
  onDone: () => void
  onRetryFailed: () => void
  retrying: boolean
}

export const SummaryStep = ({ state, onImportAnother, onDone, onRetryFailed, retrying }: Props) => {
  const business = useGetBusiness()
  // S6b: the sprint's packet, looked up rather than carried through state — one
  // packet per sprint, so this also resolves when a finished import is reopened
  // by URL (where the confirm response is long gone).
  const { list: packets } = useSprintPackets()
  const progress = state.progress
  if (!progress) return null

  const sprintPacket = progress.sprintID
    ? packets?.find(packet => packet.sprintID === progress.sprintID)
    : undefined

  const done = progress.rows.filter(row => row.status === CreativeImportRowStatus.Done)
  const failed = progress.rows.filter(row => row.status === CreativeImportRowStatus.Error)
  // Interrupted may still be redelivered, so the server excludes it too.
  const retryable =
    progress.status === CreativeImportJobStatus.CompletedWithErrors ||
    progress.status === CreativeImportJobStatus.Failed

  return (
    <div className="flex flex-col gap-4">
      {progress.status === CreativeImportJobStatus.Completed && (
        <Alert severity="success">
          {done.length} draft creative{done.length === 1 ? '' : 's'} imported.
        </Alert>
      )}
      {progress.status === CreativeImportJobStatus.CompletedWithErrors && (
        <Alert severity="warning">
          {done.length} of {progress.counts.selected} creatives imported; {failed.length} failed.
        </Alert>
      )}
      {progress.status === CreativeImportJobStatus.Failed && (
        <Alert severity="error">
          {progress.errorMessage ?? 'The import failed — no creatives were created.'}
        </Alert>
      )}
      {progress.status === CreativeImportJobStatus.Interrupted && (
        <Alert severity="warning">
          The import was interrupted — {done.length} of {progress.counts.selected} completed. The
          rest were not imported.
        </Alert>
      )}

      {done.length > 0 && (
        <div className="rounded-lg border border-mm-neutral-1-200 bg-white">
          <p className="border-b border-mm-neutral-1-200 px-4 py-2 font-medium text-mm-neutral-1-900">
            Imported drafts
          </p>
          <div className="divide-y divide-mm-neutral-1-200">
            {done.map(row =>
              row.createdAdCreativeDeleted ? (
                <p key={row.rowNumber} className="px-4 py-2 text-mm-neutral-1-500">
                  {row.effectiveName} <span className="text-xs">(deleted)</span>
                </p>
              ) : (
                <Link
                  key={row.rowNumber}
                  to={`/${business.id}/creatives/${row.createdAdCreativeID}`}
                  className="block px-4 py-2 text-mm-primary-600 hover:bg-mm-neutral-1-50 hover:underline"
                >
                  {row.effectiveName}
                </Link>
              )
            )}
          </div>
          {(progress.sprintID || sprintPacket) && (
            <div className="flex flex-col gap-1 border-t border-mm-neutral-1-200 px-4 py-2 text-sm text-mm-neutral-1-600">
              {progress.sprintID && (
                <p>
                  Attached to{' '}
                  <Link
                    to={`/${business.id}/sprints/${progress.sprintID}`}
                    className="text-mm-primary-600 hover:underline"
                  >
                    the selected sprint
                  </Link>
                  .
                </p>
              )}
              {sprintPacket && (
                <p>
                  Ready to prep in{' '}
                  <Link
                    to={`/${business.id}/sprint-packets/${sprintPacket.id}/edit`}
                    className="text-mm-primary-600 hover:underline"
                  >
                    the client packet
                  </Link>
                  .
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {failed.length > 0 && (
        <div className="rounded-lg border border-mm-neutral-1-200 bg-white p-4">
          <p className="font-medium text-mm-neutral-1-900">Failed rows</p>
          {failed.map(row => (
            <p key={row.rowNumber} className="mt-1 text-sm text-mm-neutral-1-700">
              <span className="font-medium">{row.effectiveName}</span> —{' '}
              {row.errorKind === 'fb' ? 'Facebook rejected it: ' : ''}
              {row.errorMessage ?? 'unknown error'}
            </p>
          ))}
          {retryable && (
            <div className="mt-3">
              <Button variant="outlined" onClick={onRetryFailed} disabled={retrying}>
                {retrying
                  ? 'Retrying…'
                  : `Retry ${failed.length} failed row${failed.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          )}
          <p className="mt-3 text-xs text-mm-neutral-1-500">
            {retryable
              ? 'Only the failed rows run again — anything already uploaded to Facebook is reused, not duplicated. '
              : ''}
            If the source itself is wrong, fix it and re-upload the archive, deselecting the rows
            that already succeeded
            {done.length > 0 ? ` (${done.map(row => row.effectiveName).join(', ')})` : ''}.
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <Button onClick={onImportAnother}>Import another</Button>
        <Button variant="contained" onClick={onDone}>
          Back to Creatives
        </Button>
      </div>
    </div>
  )
}
